import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../api";

/**
 * One-time session_id exchange. Lives at /auth/callback — Google sends users
 * here with `#session_id=<sid>` in the URL fragment. We POST that to the
 * backend, which validates the domain allow-list and sets the httpOnly
 * session cookie. Then we drop the fragment and redirect to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  // useRef to prevent StrictMode double-fire from consuming the session_id twice.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = (location.hash || window.location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const r = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true },
        );
        // Strip the fragment so the session_id never lingers in browser history.
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: r.data } });
      } catch (err) {
        const status = err.response?.status;
        if (status === 403) {
          // Disallowed domain — surface a friendly hint on the login page.
          navigate("/login?error=restricted", { replace: true });
        } else {
          navigate("/login", { replace: true });
        }
      }
    })();
  }, [location.hash, navigate]);

  return (
    <div data-testid="auth-callback-loading" style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--octopus-night, #0a0d1f)",
    }}>
      <div style={{ color: "var(--octopus-ice, #e7faff)", fontSize: "0.9rem", opacity: 0.8 }}>
        Signing you in…
      </div>
    </div>
  );
}
